import React, { useMemo } from "react";
import useMisinformationFingerprints from "../hooks/use-misinformation-fingerprints.hook";
import {
  Box,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { htmlDecode } from "utils/html.util";
import { isNotNullOrEmpty } from "utils/string.util";
import { Helmet } from "react-helmet-async";

const MisinformationDetailsPage = () => {
  const { fingerprintDetails, loading } = useMisinformationFingerprints();

  const title = useMemo(() => {
    if (
      fingerprintDetails == null ||
      fingerprintDetails.writeUp == null ||
      fingerprintDetails.writeUp.name == null
    ) {
      return "";
    }

    console.log(fingerprintDetails.writeUp);

    if (isNotNullOrEmpty(fingerprintDetails.writeUp.name.body[0])) {
      return `Myth: ${fingerprintDetails.writeUp.name.body}`;
    }
    return "Myth: No Name Provided";
  }, [fingerprintDetails]);

  if (loading) {
    return (
      <Box mt={2} px={2}>
        <Typography variant="h4" gutterBottom color="primary.dark">
          Myth Details
        </Typography>
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6">Basic Information</Typography>
          <Skeleton variant="text" height={40} />
          <Skeleton variant="text" height={40} />
        </Paper>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 4 }} />
      </Box>
    );
  }

  if (!fingerprintDetails) {
    return null;
  }

  const { writeUp, metaData, mythExampleLinks } = fingerprintDetails;
  const maxArticlesToShow = 2;
  const exampleLinks = mythExampleLinks?.slice(0, maxArticlesToShow) ?? [];

  const capitalizeTitle = (title) => {
    if (typeof title !== "string" || !title.trim()) {
      return "";
    }
    return title
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const renderSection = (section, idx) => {
    if (!section?.body || !Array.isArray(section.body)) return null;

    return (
      <Box key={section?.title ?? idx} mb={4}>
        <Typography mb={1} fontWeight="bold" variant="subtitle2">
          {capitalizeTitle(section?.title) ?? `Section ${idx + 1}`}
        </Typography>
        {section.body.map((bodyPoint, index) => (
          <Typography mt={1} variant="body1" key={index}>
            {bodyPoint}
          </Typography>
        ))}
        <Divider sx={{ mt: 2 }} />
      </Box>
    );
  };

  return (
    <Stack direction="column" mt={2} px={2} mb={2}>
      {!loading && writeUp?.name && writeUp?.description ? (
        <>
          <Helmet>
            <title>Sooth - False Claims Fingerprint - {title}</title>
          </Helmet>
          <Typography color="primary.dark" alignItems="center" variant="h3">
            Myth: {writeUp.name?.body || "No Name Provided"}
          </Typography>
          <Typography>{htmlDecode(writeUp.description?.body ?? "")}</Typography>
        </>
      ) : (
        <Typography color="primary.dark" alignItems="center" variant="h3">
          Myth Details
        </Typography>
      )}

      {writeUp && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h4">Fingerprint Details</Typography>
          {Object.values(writeUp)
            ?.filter(
              (section) =>
                section != null &&
                section?.title !== "Name" &&
                section?.title !== "Description"
            )
            .map((section, index) => renderSection(section, index))}
        </Paper>
      )}

      {metaData && exampleLinks.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h4">Examples</Typography>
          {exampleLinks.map(({ link }, index) => (
            <ListItem
              dense
              disableGutters
              disablePadding
              sx={{ wordWrap: "break-word" }}
              key={index}
            >
              <ListItemText
                primary={
                  <Link href={link} target="_blank" rel="noopener noreferrer">
                    {link && link.length > 100
                      ? link.substring(0, 100) + "..."
                      : link}
                  </Link>
                }
              />
            </ListItem>
          ))}
        </Paper>
      )}

      {metaData &&
        metaData?.reliableSources?.body &&
        Array.isArray(metaData.reliableSources.body) &&
        metaData.reliableSources.body.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h4">Reliable Sources</Typography>
            <List dense disablePadding>
              {metaData.reliableSources.body.map((item, index) => (
                <ListItem
                  dense
                  disableGutters
                  disablePadding
                  sx={{ wordWrap: "break-word" }}
                  key={index}
                >
                  <ListItemText
                    primary={
                      <Link
                        href={item}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.length > 100
                          ? item.substring(0, 100) + "..."
                          : item}
                      </Link>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
    </Stack>
  );
};

export default MisinformationDetailsPage;
